import { useEffect, useRef } from 'react';
import { last } from 'lodash-es';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import {
  store,
  useLocalState,
} from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { DATABRAIN_AGENT_ID2 } from '../../config/constant';
import { useAgentModelsData } from '../../AgentCollection/hooks';
import type { IAgentData } from '../../AgentCollection/types';
import type { Conversation } from '../../ChatPage/types/history';

/** 与 useCopilotOpenBridge / AgentModeSelector 保持一致 */
const EXTERNAL_TARGET_AGENT_ID_KEY = 'externalTargetAgentId';
/** 首页是否已展示过 DataBrain 2.0 默认选中 */
const HOME_SHOWN_AGENT_20_KEY = '__dba_home_shown_agent_2_0__';

/**
 * 首页 AI 智能问答默认 Agent：
 * 外链强制 id → 新建对话外部锁 → 未展示过则 DataBrain 2.0（只写一次）→ 历史第一条最后一轮 modeId
 * @param enabled - 是否处于 AI 智能问答 tab
 * @param forcedAgentId - Gallery / GameWiki 等外链指定的 Agent
 */
const useHomeDefaultAgentFromChatList = (
  enabled: boolean,
  forcedAgentId?: string,
): void => {
  const { chatList } = useChatList({ manual: true });
  const { authorizedAgents, handleAgentClick } = useAgentModelsData();
  const appliedRef = useRef('');

  const firstSessionId = chatList?.[0]?.session_id;
  const [conversations] = useLocalState<Conversation[]>(
    firstSessionId ? `__dba_chat_history_${firstSessionId}__` : '',
    [],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (forcedAgentId) {
      if (appliedRef.current === forcedAgentId) {
        return;
      }
      if (!authorizedAgents?.length) {
        return;
      }
      const isForcedValid = authorizedAgents.some(
        (agent: IAgentData) => agent.agentId === forcedAgentId,
      );
      if (!isForcedValid) {
        return;
      }
      handleAgentClick(forcedAgentId);
      appliedRef.current = forcedAgentId;
      return;
    }

    const externalAgentId = store.getItem<string>(
      EXTERNAL_TARGET_AGENT_ID_KEY,
      '',
    );
    if (externalAgentId) {
      store.setItem(EXTERNAL_TARGET_AGENT_ID_KEY, '');
      appliedRef.current = externalAgentId;
      return;
    }

    // 等待授权数据就绪，避免在 authorizedAgents 未加载完成时写入无效 agentId（与 forcedAgentId 分支保持一致）
    if (!authorizedAgents?.length) {
      return;
    }
    if (
      authorizedAgents.some(
        (agent: IAgentData) => agent.agentId === DATABRAIN_AGENT_ID2,
      ) &&
      localStorage.getItem(HOME_SHOWN_AGENT_20_KEY) !== 'true'
    ) {
      handleAgentClick(DATABRAIN_AGENT_ID2);
      localStorage.setItem(HOME_SHOWN_AGENT_20_KEY, 'true');
      appliedRef.current = DATABRAIN_AGENT_ID2;
      return;
    }

    if (!conversations.length) {
      return;
    }

    const latestModelId = last(conversations)?.modeId;
    if (!latestModelId || appliedRef.current === latestModelId) {
      return;
    }
    if (appliedRef.current) {
      return;
    }

    const isLatestValid = authorizedAgents?.some(
      (agent: IAgentData) => agent.agentId === latestModelId,
    );
    if (!isLatestValid) {
      return;
    }

    handleAgentClick(latestModelId);
    appliedRef.current = latestModelId;
  }, [
    enabled,
    forcedAgentId,
    chatList,
    firstSessionId,
    conversations,
    authorizedAgents,
    handleAgentClick,
  ]);
};

export default useHomeDefaultAgentFromChatList;
